var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var SixRules_1;
import { MGPFallible, MGPOptional, Set } from '@everyboard/lib';
import { NumberConfig } from '../../config/NumberConfig';
import { RulesConfigDescription } from '../../config/RulesConfigDescription';
import { GameNode } from '../../jscaip/AI/GameNode';
import { CoordSet } from '../../jscaip/CoordSet';
import { GameStatus } from '../../jscaip/GameStatus';
import { HexaDirection } from '../../jscaip/HexaDirection';
import { Player } from '../../jscaip/Player';
import { ConfigurableRules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { Debug } from '../../utils/Debug';
import { MGPValidators } from '../../utils/MGPValidator';
import { SixFailure } from './SixFailure';
import { SixState } from './SixState';
export class SixNode extends GameNode {
}
let SixRules = class SixRules extends ConfigurableRules {
    static { SixRules_1 = this; }
    static singleton = MGPOptional.empty();
    currentVictorySource;
    static RULES_CONFIG_DESCRIPTION = new RulesConfigDescription({
        name: () => $localize `Six`,
        config: {
            piecesPerPlayer: new NumberConfig(20, () => $localize `Number of pieces to drop per player`, MGPValidators.range(5, 99)),
        },
    });
    static get() {
        if (SixRules_1.singleton.isAbsent()) {
            SixRules_1.singleton = MGPOptional.of(new SixRules_1());
        }
        return SixRules_1.singleton.get();
    }
    getRulesConfigDescription() {
        return SixRules_1.RULES_CONFIG_DESCRIPTION;
    }
    getInitialState() {
        const board = [[Player.ZERO], [Player.ONE]];
        return SixState.ofRepresentation(board, 0);
    }
    isInDropPhase(state, config) {
        const totalDroppablePieces = 2 * config.piecesPerPlayer;
        return state.turn < totalDroppablePieces;
    }
    applyLegalMove(move, state, config, kept) {
        if (this.isInDropPhase(state, config)) {
            return state.applyLegalDrop(move.landing);
        }
        else {
            return state.applyLegalTranslation(move, kept);
        }
    }
    isLegal(move, state, config) {
        const landingLegality = state.isIllegalLandingZone(move.landing, move.start);
        if (landingLegality.isFailure()) {
            return landingLegality.toOtherFallible();
        }
        if (this.isInDropPhase(state, config)) {
            return this.isLegalDrop(move, state);
        }
        else {
            return this.isLegalPhaseTwoMove(move, state);
        }
    }
    getLegalLandings(state) {
        let neighbors = new CoordSet();
        for (const piece of state.getPieceCoords()) {
            for (const dir of HexaDirection.factory.all) {
                const neighbor = piece.getNext(dir, 1);
                if (state.getPieceAt(neighbor).isNone()) {
                    neighbors = neighbors.addElement(neighbor);
                }
            }
        }
        return neighbors.toList();
    }
    isLegalDrop(move, state) {
        if (move.isDrop() === false) {
            return MGPFallible.failure(SixFailure.CANNOT_MOVE_YET());
        }
        return MGPFallible.success(new CoordSet(state.getPieceCoords()));
    }
    isLegalPhaseTwoMove(move, state) {
        if (move.isDrop()) {
            return MGPFallible.failure(SixFailure.CAN_NO_LONGER_DROP());
        }
        const pieceOwner = state.getPieceAt(move.start.get());
        if (pieceOwner.isNone()) {
            return MGPFallible.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        else if (pieceOwner === state.getCurrentOpponent()) {
            return MGPFallible.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        const stateAfterMove = state.movePiece(move);
        const groupsAfterMove = stateAfterMove.getGroups();
        if (this.isSplit(groupsAfterMove)) {
            const biggerGroups = this.getLargestGroups(groupsAfterMove);
            if (biggerGroups.size() === 1) {
                if (move.keep.isPresent()) {
                    return MGPFallible.failure(SixFailure.CANNOT_CHOOSE_TO_KEEP());
                }
                else {
                    return MGPFallible.success(biggerGroups.getAnyElement().get());
                }
            }
            else {
                return this.moveKeepBiggerGroup(move.keep, biggerGroups, stateAfterMove);
            }
        }
        else {
            return MGPFallible.success(new CoordSet());
        }
    }
    isSplit(groups) {
        return groups.size() > 1;
    }
    getLargestGroups(groups) {
        let biggerSize = 0;
        let biggerGroups = new Set();
        for (const group of groups) {
            const groupSize = group.size();
            if (groupSize > biggerSize) {
                biggerSize = groupSize;
                biggerGroups = new Set([group]);
            }
            else if (groupSize === biggerSize) {
                biggerGroups = biggerGroups.addElement(group);
            }
        }
        return biggerGroups;
    }
    moveKeepBiggerGroup(keep, biggerGroups, state) {
        if (keep.isAbsent()) {
            return MGPFallible.failure(SixFailure.MUST_CUT());
        }
        if (state.getPieces().get(keep.get()).isAbsent()) {
            return MGPFallible.failure(SixFailure.CANNOT_KEEP_EMPTY_COORD());
        }
        const keptCoord = keep.get();
        for (const subGroup of biggerGroups) {
            if (subGroup.contains(keptCoord)) {
                return MGPFallible.success(subGroup);
            }
        }
        return MGPFallible.failure(SixFailure.MUST_CAPTURE_BIGGEST_GROUPS());
    }
    getGameStatus(node, config) {
        const state = node.gameState;
        const previousPlayer = state.getPreviousPlayer();
        if (node.previousMove.isPresent()) {
            const shapeVictory = this.getShapeVictory(node.previousMove.get(), state);
            if (shapeVictory.length === 6) {
                return GameStatus.getVictory(previousPlayer);
            }
        }
        if (this.isInDropPhase(state, config)) {
            return GameStatus.ONGOING;
        }
        else {
            const pieces = state.countPiecesOnBoard();
            const zeroPieces = pieces.get(Player.ZERO);
            const onePieces = pieces.get(Player.ONE);
            if (zeroPieces < 6 && onePieces < 6) {
                if (zeroPieces < onePieces) {
                    return GameStatus.ONE_WON;
                }
                else if (onePieces < zeroPieces) {
                    return GameStatus.ZERO_WON;
                }
                else {
                    return GameStatus.DRAW;
                }
            }
            else if (zeroPieces < 6) {
                return GameStatus.getDefeat(Player.ZERO);
            }
            else if (onePieces < 6) {
                return GameStatus.getDefeat(Player.ONE);
            }
            else {
                return GameStatus.ONGOING;
            }
        }
    }
    startSearchingVictorySources() {
        this.currentVictorySource = {
            typeSource: 'LINE',
            index: -1,
        };
    }
    getShapeVictory(lastMove, state) {
        this.startSearchingVictorySources();
        while (this.hasNextVictorySource()) {
            this.getNextVictorySource();
            const shapeVictory = this.searchVictoryOnly(this.currentVictorySource, lastMove, state);
            if (shapeVictory.length === 6) {
                return shapeVictory;
            }
        }
        return [];
    }
    hasNextVictorySource() {
        return this.currentVictorySource.typeSource !== 'CIRCLE' ||
            this.currentVictorySource.index !== 5;
    }
    getNextVictorySource() {
        const source = this.currentVictorySource;
        if (source.index === 5) {
            let newType;
            switch (this.currentVictorySource.typeSource) {
                case 'LINE':
                    newType = 'TRIANGLE_CORNER';
                    break;
                case 'TRIANGLE_CORNER':
                    newType = 'TRIANGLE_EDGE';
                    break;
                default:
                    newType = 'CIRCLE';
                    break;
            }
            this.currentVictorySource = {
                typeSource: newType,
                index: 0,
            };
        }
        else {
            let increment = 1;
            if (this.currentVictorySource.typeSource === 'LINE') {
                increment = 2;
            }
            this.currentVictorySource = {
                typeSource: this.currentVictorySource.typeSource,
                index: this.currentVictorySource.index + increment,
            };
        }
        return this.currentVictorySource;
    }
    searchVictoryOnly(victorySource, move, state) {
        const lastDrop = move.landing;
        switch (victorySource.typeSource) {
            case 'LINE':
                return this.searchVictoryOnlyForLine(victorySource.index, lastDrop, state);
            case 'CIRCLE':
                return this.searchVictoryOnlyForCircle(victorySource.index, lastDrop, state);
            case 'TRIANGLE_CORNER':
                return this.searchVictoryOnlyForTriangleCorner(victorySource.index, lastDrop, state);
            default:
                return this.searchVictoryOnlyForTriangleEdge(victorySource.index, lastDrop, state);
        }
    }
    searchVictoryOnlyForCircle(index, lastDrop, state) {
        const previousPlayer = state.getPreviousPlayer();
        const initialDirection = HexaDirection.factory.all[index];
        const victory = [lastDrop];
        let testCoord = lastDrop.getNext(initialDirection, 1);
        while (victory.length < 6) {
            const testedPiece = state.getPieceAt(testCoord);
            if (testedPiece !== previousPlayer) {
                return [];
            }
            const dirIndex = (index + victory.length) % 6;
            victory.push(testCoord);
            const dir = HexaDirection.factory.all[dirIndex];
            testCoord = testCoord.getNext(dir, 1);
        }
        return victory;
    }
    searchVictoryOnlyForLine(index, lastDrop, state) {
        const previousPlayer = state.getPreviousPlayer();
        let dir = HexaDirection.factory.all[index];
        let testCoord = lastDrop.getNext(dir, 1);
        const victory = [lastDrop];
        let twoDirectionCovered = false;
        while (victory.length < 6) {
            const testedPiece = state.getPieceAt(testCoord);
            if (testedPiece === previousPlayer) {
                victory.push(testCoord);
            }
            else {
                if (twoDirectionCovered) {
                    return [];
                }
                else {
                    twoDirectionCovered = true;
                    dir = dir.getOpposite();
                    testCoord = testCoord.getNext(dir, victory.length);
                }
            }
            testCoord = testCoord.getNext(dir, 1);
        }
        return victory;
    }
    searchVictoryOnlyForTriangleCorner(index, lastDrop, state) {
        const previousPlayer = state.getPreviousPlayer();
        let edgeDirection = HexaDirection.factory.all[index];
        const victory = [lastDrop];
        let testCoord = lastDrop.getNext(edgeDirection, 1);
        while (victory.length < 6) {
            // Testing the corner
            const testedPiece = state.getPieceAt(testCoord);
            if (testedPiece !== previousPlayer) {
                return [];
            }
            if (victory.length % 2 === 0) {
                // reached a corner, let's turn
                const dirIndex = (index + victory.length) % 6;
                edgeDirection = HexaDirection.factory.all[dirIndex];
            }
            victory.push(testCoord);
            testCoord = testCoord.getNext(edgeDirection, 1);
        }
        return victory;
    }
    searchVictoryOnlyForTriangleEdge(index, lastDrop, state) {
        const previousPlayer = state.getPreviousPlayer();
        let edgeDirection = HexaDirection.factory.all[index];
        const victory = [lastDrop];
        let testCoord = lastDrop.getNext(edgeDirection, 1);
        while (victory.length < 6) {
            // Testing the corner
            const testedPiece = state.getPieceAt(testCoord);
            if (testedPiece !== previousPlayer) {
                return [];
            }
            victory.push(testCoord);
            if (victory.length % 2 === 0) {
                // reached a corner, let's turn
                const dirIndex = (index + victory.length) % 6;
                edgeDirection = HexaDirection.factory.all[dirIndex];
            }
            testCoord = testCoord.getNext(edgeDirection, 1);
        }
        return victory;
    }
};
SixRules = SixRules_1 = __decorate([
    Debug.log
], SixRules);
export { SixRules };
